import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import { VictoryContainer, VictoryPie, VictoryTooltip, VictoryLabel } from 'victory';
import sizes from '../sizes.js';
import colors from '../../../utils/colors.js';
import { useMaturityLevel } from '../../../component-maturity.js';

const parse = ({ data, top, others, empty, isEmpty, format, disableAutoSorting }) => {
  if (isEmpty) {
    return [{
      value: 1,
      tooltip: empty,
      color: colors.grey200
    }];
  }
  const topCount = _.max([+top || _.get(data, 'length') || 0, 1]);
  const withNumericValues = _.map(data, (original) => _.merge({}, original, { value: (+original.value || 0) }));
  const sorted = disableAutoSorting ? withNumericValues : _.orderBy(withNumericValues, ['value'], ['desc']);
  const withColors = _.map(sorted, ({ value, color, label, tooltip }, index) => {
    return {
      value,
      label,
      tooltip,
      color: color || _.get(colors.order, index % colors.order.length, 'black')
    };
  });
  if (!top) {
    return withColors;
  }
  const onlyTop = _.take(withColors, topCount);
  if (others === false) {
    return onlyTop;
  }
  const remainder = _.reduce(_.without(withColors, ...onlyTop), (total, { value }) => (total + +value), 0);
  const othersLabel = _.isString(others) ? others : 'Other';
  if ((remainder <= 0) || !_.some(_.trim(othersLabel))) {
    return onlyTop;
  }
  return _.concat([], onlyTop, [{
    value: remainder,
    tooltip: [othersLabel, _.method('others', remainder)(format)],
    label: othersLabel,
    color: colors.other
  }]);
};

// this is needed because of how victory calculates layout
const hardcodedTooltip = { fontSize: 9 };
const Donut = ({
  size,
  openTooltips,
  className,
  data,
  empty,
  top,
  others,
  width,
  height,
  innerRadius,
  lineHeight,
  total,
  format,
  disableAutoSorting
}) => {
  useMaturityLevel('deprecated', 'Donut');
  const sizedWidth = width || sizes.radius(size);
  const sizedHeight = height || sizes.radius(size);
  const sizedInnerRadius = innerRadius || sizes.innerRadius(size);
  const isEmpty = !_.some(data);
  const formatWithDefaults = _.merge({ others: _.identity, total: _.identity }, format);
  const parsed = parse({ data, top, others, empty, isEmpty, disableAutoSorting, format: formatWithDefaults });
  const totalValue = isEmpty ? 0 : _.reduce(parsed, (sum, { value }) => (sum + +value), 0);
  const totalLabel = _.isString(total) ? total : 'Total';
  const classes = classnames('particles-donut-chart', className, {
    'particles-donut-chart--empty': !!isEmpty
  });
  const totalClasses = classnames('particles-donut-chart__total', {
    'particles-donut-chart__total--empty': !!isEmpty
  });
  const tooltipClasses = classnames('particles-donut-chart__tooltip', {
    'particles-donut-chart__tooltip--empty': !!isEmpty
  });
  return (
    <div className={classes}>
      <VictoryContainer responsive={false} style={{ overflow: 'visible', margin: '0 auto' }} height={sizedHeight} width={sizedWidth}>
        <VictoryPie
          standalone={false}
          containerComponent={<VictoryContainer responsive={false} style={{ overflow: 'visible' }} />}
          width={sizedWidth}
          height={sizedHeight}
          data={parsed}
          style={{
            parent: { margin: '0', padding: '0', maxWidth: '100%', overflow: 'visible' },
            data: {
              fill: ({ color } = {}) => color,
              stroke: ({ color } = {}) => color
            }
          }}
          innerRadius={sizedInnerRadius}
          labelComponent={(
            <VictoryTooltip
              active={openTooltips}
              labelComponent={
                <VictoryLabel
                  style={hardcodedTooltip}
                  className={tooltipClasses}
                  lineHeight={lineHeight}
                />
              }
              text={({ tooltip, label, value }) => (tooltip || label || value)}
              flyoutStyle={{ stroke: ({ color } = {}) => color, fill: 'white' }}
            />
          )}
          x="label"
          y="value"
        />
        {((total !== false) && _.some(_.trim(totalLabel))) && (
          <VictoryLabel
            textAnchor="middle"
            verticalAnchor="middle"
            x={sizedWidth / 2}
            y={sizedHeight / 2}
            className={totalClasses}
            lineHeight={lineHeight}
            text={[totalLabel, _.method('total', totalValue)(formatWithDefaults)]}
          />
        )}
      </VictoryContainer>
    </div>
  );
};

Donut.propTypes = {
  /** Disable automatical sorting of values*/
  disableAutoSorting: PropTypes.bool,
  /** Value display formatter. You can pass a function for both `total` and `others` values. Your custom
   * function is passed a value and should return a string.
   * ie: `(value) => string`
   */
  format: PropTypes.shape({
    total: PropTypes.func,
    others: PropTypes.func
  }),
  /** Size of the chart, ['small', 'medium', 'large'] */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Additional class to add to component */
  className: PropTypes.string,
  /** Toggle display of tooltips */
  openTooltips: PropTypes.bool,
  /** Specify a static width in pixels of chart. NOTE: Not recommended to modify in normal circumstances */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Specify a static height in pixels of chart. NOTE: Not recommended to modify in normal circumstances */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Inner radious of donut. NOTE: Not recommended to modify in normal circumstances */
  innerRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Line height for text in the chart. NOTE: Not recommended to modify in normal circumstances */
  lineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Placeholder text to show when chart has no data */
  empty: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Show only the top x values */
  top: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  /** Label for total value of the chart */
  total: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Label for the others value (values excluded from total) */
  others: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Array of data to display in chart. Each item in the array can have the following keys
   * "label", "color", "value", "tooltip".
  */
  data: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    color: PropTypes.oneOfType([PropTypes.string]),
    label: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    tooltip: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
  })).isRequired
};

Donut.defaultProps = {
  disableAutoSorting: false,
  format: {
    total: _.identity,
    others: _.identity
  },
  size: 'medium',
  openTooltips: false,
  className: '',
  lineHeight: 2,
  top: 3,
  empty: 'No Data',
  others: 'Other',
  total: 'Total',
  data: []
};

Donut.displayName = 'Chart.Donut';

export default Donut;

export const {
  propTypes,
  defaultProps,
  displayName
} = Donut;
